const VolumeControlHandler = require("./WebSerialVolumeControlHandler");
const WebSerialDisplayControl = require("./WebSerialDisplayControl");
const WebSerialForgetHandler = require("./WebSerialForgetHandler");
const WebSerialScreenStatusService = require("./WebSerialScreenStatusService");

const Logger = require("../Logger");

const INVALID_DISPLAY_CONTROL_CONFIGURATION = "E000000107";

const service = { webSerialDisplayControl: null };

service.parseDisplayControlConfiguration = configurationText => {
  if( !configurationText ) {
    return null;
  }

  return configurationText.split("\n")
    .filter(line => line.includes('='))
    .map(line => line.trim().split('='))
    .reduce((settings, [key, value]) =>
      ({ ...settings, [key]: value }), {}
   );
};

const configurePowerCommands = settings => {
  try {
    console.warn( "Only configuring power commands", settings );

    const serialPortCommunicationHandler = VolumeControlHandler.getSerialPortCommunicationHandler();
    WebSerialDisplayControl.configurePowerCommands( settings, serialPortCommunicationHandler );

    service.webSerialDisplayControl = WebSerialDisplayControl;

    return Promise.resolve( true );
  } catch( error ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Could not configure power commands",
      { settings, error: error && ( error.stack || error.message ) }
    );

    return Promise.resolve( false );
  }
};

const leaveAsDisabled = () => {
  service.stopDisplayControlService();
  WebSerialForgetHandler.setDisplayControlEnabled( false );

  return Promise.resolve();
}

const configureDisplayControl = ( settings, configurationText ) => {
  // If volume control was configured before display control, set the power commands, and use the existing connection.
  if( WebSerialDisplayControl.isConfigured() && !WebSerialDisplayControl.powerCommandsAvailable() ) {
    return configurePowerCommands( settings );
  }

  return WebSerialDisplayControl.configure( settings )
  .then(() => {
    service.webSerialDisplayControl = WebSerialDisplayControl;

    return true;
  }).catch((error) => {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Could not configure display control",
      { settings: configurationText, error: error && ( error.stack || error.message ) }
    );

    return false;
  });
}

const configureScreenStatusService = () => {
  return Promise.resolve().then(() => {
    return WebSerialScreenStatusService.configure()
    .then(screenStatusServiceConfigured => {
      if( screenStatusServiceConfigured ) {
        WebSerialScreenStatusService.start();
      }
    });
  }).catch(error => {
    Logger.viewerError(
      "E999999999",
      "Could not configure screen status service",
      { error: error && ( error.stack || error.message ) }
    );
  });
}

const CONNECTION_READY_LISTENER = {
  onSerialConnectionReady: () => {
    // No need to wait for the promise to resolve here; it's handled in the configureScreenStatusService function
    configureScreenStatusService();

    // No longer necessary to listen for serial connection ready event
    unregisterSerialConnectionReadyListener();
  }
};

const registerSerialConnectionReadyListener = () => {
  WebSerialDisplayControl.addSerialConnectionReadyListener( CONNECTION_READY_LISTENER );
};

const unregisterSerialConnectionReadyListener = () => {
  WebSerialDisplayControl.removeSerialConnectionReadyListener( CONNECTION_READY_LISTENER );
};

service.configure = configurationText => {
  const settings = service.parseDisplayControlConfiguration(configurationText);

  if( !settings || !settings.interface ) {
    console.warn("Display control configuration not provided");

    return leaveAsDisabled();
  }

  if( settings.interface.toUpperCase() !== "RS232" ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported display control interface value",
      configurationText
    );

    return leaveAsDisabled();
  }

  WebSerialForgetHandler.setDisplayControlEnabled( true );

  if( service.webSerialDisplayControl ) {
    // At this point changing configuration would require restarting Rise Vision Player
    console.warn("Display control service already configured, skipping");
    return Promise.resolve();
  }

  return configureDisplayControl( settings, configurationText )
  .then(success => {
    if( success ) {
      if( !WebSerialDisplayControl.isSerialConnectionReady() ) {
        console.warn( "Connection not ready; screen status service won't configure until it's ready" );
    
        registerSerialConnectionReadyListener();
        return;
      }

      return configureScreenStatusService();
    }
  });
};

service.stopDisplayControlService = () => {
  WebSerialScreenStatusService.stop();
  unregisterSerialConnectionReadyListener();

  if( !service.webSerialDisplayControl ) {
    return;
  }

  console.log( "Stopping display control service" );

  if( VolumeControlHandler.isVolumeControlEnabled() ) {
    console.warn( "Volume control is using the same connection, so instead of stopping the serial connection, we will just reset the power commands" );
    service.webSerialDisplayControl.resetPowerCommands();
  } else {
    service.webSerialDisplayControl.stop();
  }

  service.webSerialDisplayControl = null;
};

service.turnDisplayOn = () => {
  if( !service.webSerialDisplayControl ) {
    return Promise.resolve();
  }

  return service.webSerialDisplayControl.turnDisplayOn();
};

service.turnDisplayOff = () => {
  if( !service.webSerialDisplayControl ) {
    return Promise.resolve();
  }

  return service.webSerialDisplayControl.turnDisplayOff();
};

module.exports = service;
